import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useAuth } from '../store';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const API = (() => {
  const h = window.location.hostname;
  return process.env.REACT_APP_API_URL || ((h === 'localhost' || h === '127.0.0.1') ? 'http://localhost:5001/api' : `http://${h}:5001/api`);
})();

// ── Types ──────────────────────────────────────────────────────────────────────

interface Branch { id: string; name: string; code: string; city?: string; }

interface Addon {
  id: string;
  branch_id: string;
  name: string;
  description?: string;
  weekday_price: number;
  weekend_price: number;
  is_active: boolean;
  is_rental: boolean;
  addon_type?: string;
  stock_quantity: number;
  gudang_quantity: number;
  chiller_quantity: number;
  weekday_cost?: number;
  unit?: string;
  unit_besar?: string;
  konversi?: number;
  product_id?: string | null;
  product_name?: string | null;
  product_sku?: string | null;
  product_unit?: string | null;
  image_url?: string | null;
  kode_barang?: string | null;
}

interface Product {
  id: string;
  name: string;
  sku: string;
  unit: string;
  category_name?: string;
}

const EXCLUDE_CATEGORIES = ['lapangan', 'sewa', 'slot'];
const fmt = (v: number) => new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', minimumFractionDigits: 0 }).format(v);
const emptyAddonForm = { name: '', description: '', kode_barang: '', price: '', hpp: '', harga_jual: '', tipe: 'jasa', product_id: '', unit: 'pcs', unit_besar: '', konversi: '' };
const emptyMutForm = { addonId: '', type: 'in', jumlahDus: '', jumlahPcs: '', notes: '' };

// ── Component ──────────────────────────────────────────────────────────────────

export const StockPage: React.FC = () => {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const isSuperAdmin = ['super_admin', 'finance', 'accounting'].includes(user?.role || '');

  const [tab, setTab] = useState<'addons' | 'stok'>('addons');
  const [branches, setBranches] = useState<Branch[]>([]);
  const [selectedBranchId, setSelectedBranchId] = useState(user?.branch_id || '');
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState('');

  // ── Add-Ons tab state ──
  const [addons, setAddons] = useState<Addon[]>([]);
  const [addonLoading, setAddonLoading] = useState(false);
  const [showAddonForm, setShowAddonForm] = useState(false);
  const [addonForm, setAddonForm] = useState(emptyAddonForm);
  const [editingAddonId, setEditingAddonId] = useState<string | null>(null);
  const [addonSaving, setAddonSaving] = useState(false);
  const [addonImageFile, setAddonImageFile] = useState<File | null>(null);
  const [addonImagePreview, setAddonImagePreview] = useState<string | null>(null);
  const [costHistory, setCostHistory] = useState<any[]>([]);
  const [showCostHistory, setShowCostHistory] = useState(false);

  // ── Stok tab state ──
  const [allProducts, setAllProducts] = useState<Product[]>([]);
  const [showMutForm, setShowMutForm] = useState(false);
  const [mutForm, setMutForm] = useState(emptyMutForm);
  const [mutSaving, setMutSaving] = useState(false);
  const [mutError, setMutError] = useState('');
  // Quick inline stock input
  const [quickInput, setQuickInput] = useState<Record<string, string>>({});
  // Transfer Gudang → Chiller modal
  const [transferModal, setTransferModal] = useState<{ addonId: string; addonName: string; gudangQty: number; konversi: number; unitBesar: string; unit: string } | null>(null);
  const [transferDus, setTransferDus] = useState('');
  const [transferPcs, setTransferPcs] = useState('');
  const [transferSaving, setTransferSaving] = useState(false);

  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  useEffect(() => { if (!user) navigate('/login'); }, [user]);

  // Load branches + products once
  useEffect(() => {
    Promise.all([
      axios.get(`${API}/branches`, { headers }).then(r => r.data.data || r.data).catch(() => []),
      axios.get(`${API}/products`, { headers }).then(r => r.data.data || []).catch(() => []),
    ]).then(([b, p]) => {
      const branchList = Array.isArray(b) ? b : [];
      setBranches(branchList);
      if (!selectedBranchId && branchList.length > 0) {
        setSelectedBranchId(isSuperAdmin ? branchList[0].id : (user?.branch_id || branchList[0].id));
      }
      setAllProducts(Array.isArray(p) ? p : []);
    }).finally(() => setLoading(false));
  }, [token]);

  // Load addons when branch changes
  const fetchAddons = useCallback(() => {
    if (!selectedBranchId) return;
    setAddonLoading(true);
    axios.get(`${API}/pricing/addons/branch/${selectedBranchId}?all=true`, { headers })
      .then(r => {
        const list = Array.isArray(r.data.data) ? r.data.data : (Array.isArray(r.data) ? r.data : []);
        setAddons(list);
      })
      .catch(() => setAddons([]))
      .finally(() => setAddonLoading(false));
  }, [selectedBranchId, headers]);

  useEffect(() => { fetchAddons(); }, [fetchAddons]);

  // ── Search state ──────────────────────────────────────────────────────────
  const [searchAddons, setSearchAddons] = useState('');
  const [searchStok, setSearchStok] = useState('');

  // ── Derived data ──────────────────────────────────────────────────────────
  const konsumsiAddons = useMemo(() => addons.filter(a => a.is_rental === false && a.addon_type !== 'fnb'), [addons]);

  const filteredAddons = useMemo(() => {
    const q = searchAddons.trim().toLowerCase();
    if (!q) return addons;
    return addons.filter(a => a.name.toLowerCase().includes(q) || (a.description || '').toLowerCase().includes(q));
  }, [addons, searchAddons]);

  const filteredStok = useMemo(() => {
    const q = searchStok.trim().toLowerCase();
    if (!q) return konsumsiAddons;
    return konsumsiAddons.filter(a => a.name.toLowerCase().includes(q));
  }, [konsumsiAddons, searchStok]);
  const addonProducts = useMemo(() =>
    allProducts.filter(p => !EXCLUDE_CATEGORIES.some(c => (p.category_name || '').toLowerCase().includes(c))),
    [allProducts]);
  const selectedBranch = branches.find(b => b.id === selectedBranchId);

  // ── Add-Ons handlers ──────────────────────────────────────────────────────
  const openCreateAddon = () => { setAddonForm(emptyAddonForm); setEditingAddonId(null); setShowAddonForm(true); setMsg(''); setAddonImageFile(null); setAddonImagePreview(null); };
  const openEditAddon = (a: Addon) => {
    setAddonForm({
      name: a.name, description: a.description || '',
      kode_barang: (a as any).kode_barang || '',
      price: String(a.weekday_price),
      hpp: String(a.weekday_cost || ''),
      harga_jual: String(a.weekday_price || ''),
      tipe: a.addon_type || (a.is_rental === false ? 'konsumsi' : 'jasa'),
      product_id: a.product_id || '',
      unit: a.unit || 'pcs',
      unit_besar: a.unit_besar || '',
      konversi: a.konversi && a.konversi > 1 ? String(a.konversi) : '',
    });
    setAddonImageFile(null);
    setAddonImagePreview(a.image_url ? `${API}/pricing/addons/${a.id}/image?t=${Date.now()}` : null);
    setEditingAddonId(a.id); setShowAddonForm(true); setMsg('');
  };
  const cancelAddonForm = () => { setShowAddonForm(false); setEditingAddonId(null); };

  const saveAddon = async () => {
    if (!addonForm.name.trim()) { setMsg('❌ Nama add-on wajib diisi'); return; }
    if (!addonForm.price) { setMsg('❌ Harga wajib diisi'); return; }
    const isKonsumsi = addonForm.tipe === 'konsumsi' || addonForm.tipe === 'peralatan';
    // HPP validation disabled temporarily for testing
    // if (isKonsumsi && !addonForm.hpp) { setMsg('❌ HPP wajib diisi untuk tipe Konsumsi'); return; }
    const hpp = addonForm.hpp ? Number(addonForm.hpp) : 0;
    const hargaJual = Number(addonForm.price);
    if (hpp && hpp >= hargaJual) { setMsg('❌ HPP harus lebih kecil dari Harga Jual'); return; }
    setAddonSaving(true); setMsg('');
    const addonPrice = hargaJual;
    const payload = {
      branch_id: selectedBranchId,
      name: addonForm.name.trim(),
      description: addonForm.description.trim() || undefined,
      kode_barang: addonForm.kode_barang.trim() || undefined,
      weekday_price: addonPrice,
      weekend_price: addonPrice,
      weekday_cost: hpp,
      weekend_cost: hpp,
      addon_type: addonForm.tipe,
      product_id: isKonsumsi && addonForm.product_id ? addonForm.product_id : null,
      unit: isKonsumsi ? (addonForm.unit || 'pcs') : undefined,
      unit_besar: isKonsumsi && addonForm.unit_besar.trim() ? addonForm.unit_besar.trim() : null,
      konversi: isKonsumsi && addonForm.konversi ? parseInt(addonForm.konversi) || 1 : undefined,
    };
    try {
      let savedId = editingAddonId;
      if (editingAddonId) {
        await axios.put(`${API}/pricing/addons/${editingAddonId}`, payload, { headers });
      } else {
        const res = await axios.post(`${API}/pricing/addons`, payload, { headers });
        savedId = (res.data.data || res.data)?.id || null;
      }
      if (addonImageFile && savedId) {
        const fd = new FormData();
        fd.append('file', addonImageFile);
        await axios.post(`${API}/pricing/addons/${savedId}/image`, fd, { headers: { ...headers, 'Content-Type': 'multipart/form-data' } });
      }
      setMsg('✅ Add-on berhasil disimpan');
      cancelAddonForm(); fetchAddons();
    } catch (err: any) {
      setMsg(`❌ ${err.response?.data?.error || 'Gagal menyimpan'}`);
    } finally { setAddonSaving(false); }
  };

  const toggleAddonActive = async (a: Addon) => {
    try {
      await axios.put(`${API}/pricing/addons/${a.id}`, { ...a, is_active: !a.is_active }, { headers });
      setMsg(`✅ Add-on ${a.is_active ? 'dinonaktifkan' : 'diaktifkan'}`);
      fetchAddons();
    } catch { setMsg('❌ Gagal mengubah status'); }
  };

  const deleteAddon = async (a: Addon) => {
    if (!window.confirm(`Hapus add-on "${a.name}"?`)) return;
    try {
      await axios.delete(`${API}/pricing/addons/${a.id}`, { headers });
      setMsg('✅ Add-on dihapus'); fetchAddons();
    } catch { setMsg('❌ Gagal menghapus'); }
  };

  // ── Stok handlers ─────────────────────────────────────────────────────────
  const submitMutation = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!mutForm.addonId) { setMutError('Pilih add-on terlebih dahulu'); return; }
    const selectedAddon = konsumsiAddons.find(a => a.id === mutForm.addonId);
    const konversi = selectedAddon?.konversi && selectedAddon.konversi > 1 ? selectedAddon.konversi : 1;
    const jumlahDus = parseInt(mutForm.jumlahDus) || 0;
    const jumlahPcs = parseInt(mutForm.jumlahPcs) || 0;
    const totalQty = (jumlahDus * konversi) + jumlahPcs;
    if (totalQty < 1) { setMutError('Masukkan jumlah Dus atau PCS'); return; }
    setMutSaving(true); setMutError('');
    try {
      await axios.post(
        `${API}/pricing/addons/${mutForm.addonId}/stock`,
        { type: mutForm.type, quantity: totalQty, notes: mutForm.notes || null },
        { headers }
      );
      setShowMutForm(false); setMutForm(emptyMutForm);
      setMsg('✅ Stok berhasil diperbarui');
      fetchAddons();
    } catch (err: any) {
      setMutError(err.response?.data?.error || 'Gagal memperbarui stok');
    } finally { setMutSaving(false); }
  };

  const openMutFormForAddon = (a: Addon) => {
    setMutForm({ ...emptyMutForm, addonId: a.id });
    setShowMutForm(true);
    setMutError('');
  };

  const quickAdjust = async (addonId: string, dir: 'add' | 'sub') => {
    const qty = parseInt(quickInput[addonId] || '1') || 1;
    const type = dir === 'add' ? 'chiller_in' : 'chiller_out';
    try {
      await axios.post(`${API}/pricing/addons/${addonId}/stock`, { type, quantity: qty }, { headers });
      setMsg(`✅ Chiller ${dir === 'add' ? `+${qty}` : `−${qty}`}`);
      setQuickInput(prev => ({ ...prev, [addonId]: '' }));
      fetchAddons();
    } catch (err: any) {
      setMsg(`❌ ${err.response?.data?.error || 'Gagal'}`);
    }
  };

  const handleTransfer = async () => {
    if (!transferModal) return;
    const { konversi, unitBesar, unit, gudangQty, addonId, addonName } = transferModal;
    const hasDus = konversi > 1 && unitBesar;
    const dus = parseInt(transferDus) || 0;
    const pcs = parseInt(transferPcs) || 0;
    const totalQty = hasDus ? (dus * konversi + pcs) : pcs;
    if (totalQty < 1) { setMsg('❌ Jumlah transfer harus minimal 1'); return; }
    if (totalQty > gudangQty) {
      setMsg(`❌ Stok Gudang hanya ${gudangQty} ${unit} (${Math.floor(gudangQty / konversi)} ${unitBesar} ${gudangQty % konversi} ${unit})`);
      return;
    }
    setTransferSaving(true);
    try {
      await axios.post(`${API}/pricing/addons/${addonId}/stock`, { type: 'transfer', quantity: totalQty }, { headers });
      const label = hasDus
        ? `${dus > 0 ? `${dus} ${unitBesar} ` : ''}${pcs > 0 ? `${pcs} ${unit}` : ''} (${totalQty} ${unit})`
        : `${totalQty} ${unit}`;
      setMsg(`✅ Transfer ${label} ${addonName} ke Chiller berhasil`);
      setTransferModal(null);
      setTransferDus('');
      setTransferPcs('');
      fetchAddons();
    } catch (err: any) {
      setMsg(`❌ ${err.response?.data?.error || 'Gagal transfer'}`);
    } finally { setTransferSaving(false); }
  };

  // ── Render ────────────────────────────────────────────────────────────────
  if (loading) return (
    <div style={s.centered}><div style={s.spinner} /><p style={{ color: '#64748b', marginTop: 12 }}>Memuat data...</p></div>
  );

  return (
    <div style={s.container}>
      {/* Header */}
      <div style={s.header}>
        <div>
          <h1 style={s.headerTitle}>📦 Add-Ons & Stok</h1>
          <p style={s.headerSub}>Kelola daftar add-on dan stok konsumsi per cabang</p>
        </div>
        {isSuperAdmin ? (
          <select style={s.branchSelect} value={selectedBranchId} onChange={e => setSelectedBranchId(e.target.value)}>
            {branches.map(b => <option key={b.id} value={b.id}>{b.name}{b.city ? ` — ${b.city}` : ''}</option>)}
          </select>
        ) : (
          <div style={s.branchBadge}>{selectedBranch?.name || 'Cabang Anda'}</div>
        )}
      </div>

      {msg && (
        <div style={{ ...s.msg, background: msg.startsWith('✅') ? '#d4edda' : '#f8d7da', color: msg.startsWith('✅') ? '#155724' : '#721c24' }}>
          {msg}
        </div>
      )}

      {/* Tabs */}
      <div style={s.tabBar}>
        <button style={{ ...s.tabBtn, ...(tab === 'addons' ? s.tabActive : {}) }} onClick={() => setTab('addons')}>
          ➕ Daftar Add-Ons <span style={s.tabCount}>{addons.length}</span>
        </button>
        <button style={{ ...s.tabBtn, ...(tab === 'stok' ? s.tabActive : {}) }} onClick={() => setTab('stok')}>
          📦 Stok Konsumsi <span style={s.tabCount}>{konsumsiAddons.length}</span>
        </button>
      </div>

      {/* ═══════════════ TAB: ADD-ONS ═══════════════ */}
      {tab === 'addons' && (
        <div>
          <div style={s.actionBar}>
            <p style={s.actionBarText}>{filteredAddons.length} add-on · {filteredAddons.filter(a => a.is_active).length} aktif</p>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <input
                style={s.searchInput}
                placeholder="Cari add-on..."
                value={searchAddons}
                onChange={e => setSearchAddons(e.target.value)}
              />
              <button style={s.btnPrimary} onClick={openCreateAddon}>+ Tambah Add-On</button>
            </div>
          </div>

          {/* Add-On Form — Floating Modal */}
          {showAddonForm && (
            <div style={s.overlay}>
            <div style={{ ...s.modal, maxWidth: 680, maxHeight: '90vh', overflowY: 'auto' as const, padding: 28 }}>
              <h3 style={{ ...s.formTitle, marginBottom: 16 }}>{editingAddonId ? '✏️ Edit Add-On' : '➕ Tambah Add-On Baru'}</h3>
              <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
                {[
                  { value: 'jasa', label: '🎾 Jasa / Sewa', desc: 'Fotografer, wasit, raket — tidak kurangi stok' },
                  { value: 'konsumsi', label: '🥤 Konsumsi / Barang', desc: 'Air minum, barang — otomatis kurangi stok saat terjual' },
                  { value: 'fnb', label: '🍔 FNB', desc: 'Makanan & minuman — otomatis kurangi stok saat terjual' },
                ].map(opt => (
                  <button key={opt.value} type="button"
                    onClick={() => setAddonForm({ ...addonForm, tipe: opt.value, product_id: '' })}
                    style={{ flex: 1, padding: '10px 12px', borderRadius: 8, cursor: 'pointer', textAlign: 'left' as const,
                      border: addonForm.tipe === opt.value ? '2px solid #1A73E8' : '1px solid #d1d5db',
                      background: addonForm.tipe === opt.value ? '#EEF4FF' : '#fff' }}>
                    <div style={{ fontWeight: 700, fontSize: 13, color: addonForm.tipe === opt.value ? '#1A73E8' : '#374151' }}>{opt.label}</div>
                    <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>{opt.desc}</div>
                  </button>
                ))}
              </div>
              <div style={s.formGrid}>
                <div>
                  <label style={s.label}>Nama Add-On *</label>
                  <input style={s.input} value={addonForm.name} onChange={e => setAddonForm({ ...addonForm, name: e.target.value })}
                    placeholder={addonForm.tipe === 'konsumsi' ? 'Contoh: Prima (DUS), Prima BTL' : 'Contoh: Fotografer, Wasit'} />
                </div>
                <div>
                  <label style={s.label}>Deskripsi</label>
                  <input style={s.input} value={addonForm.description} onChange={e => setAddonForm({ ...addonForm, description: e.target.value })} placeholder="Opsional" />
                </div>
                <div>
                  <label style={s.label}>Kode Barang</label>
                  <input style={s.input} value={addonForm.kode_barang} onChange={e => setAddonForm({ ...addonForm, kode_barang: e.target.value })} placeholder="Contoh: ES001, AIR-BTL" />
                </div>
                {isSuperAdmin && (
                  <div>
                    <label style={s.label}>HPP / Harga Beli (Rp)</label>
                    <input style={s.input} type="number" value={addonForm.hpp} onChange={e => setAddonForm({ ...addonForm, hpp: e.target.value })} placeholder="10000" />
                    <span style={{ fontSize: 11, color: '#64748b', marginTop: 4 }}>Cost Price</span>
                  </div>
                )}
                <div>
                  <label style={s.label}>Harga Jual (Rp) *</label>
                  <input style={s.input} type="number" value={addonForm.harga_jual || addonForm.price} onChange={e => setAddonForm({ ...addonForm, harga_jual: e.target.value, price: e.target.value })} placeholder="25000" />
                  <span style={{ fontSize: 11, color: '#64748b', marginTop: 4 }}>Selling Price</span>
                </div>
                <div>
                  <label style={s.label}>Laba (Rp)</label>
                  <div style={{ padding: '10px 12px', border: '1px solid #e2e8f0', borderRadius: 6, background: '#f9f9f9', fontWeight: 700, color: (Number(addonForm.harga_jual || addonForm.price || 0) - Number(addonForm.hpp || 0)) >= 0 ? '#27ae60' : '#e74c3c', minHeight: '40px', display: 'flex', alignItems: 'center' }}>
                    {(Number(addonForm.harga_jual || addonForm.price || 0) - Number(addonForm.hpp || 0)) >= 0 ? '+' : '−'}Rp {Math.abs(Number(addonForm.harga_jual || addonForm.price || 0) - Number(addonForm.hpp || 0)).toLocaleString('id-ID')}
                  </div>
                  <span style={{ fontSize: 11, color: '#94a3b8', marginTop: 4 }}>Auto-calculated</span>
                </div>
              </div>
              {(addonForm.tipe === 'konsumsi' || addonForm.tipe === 'peralatan') && (
                <div style={{ marginBottom: 16, padding: '12px 14px', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8 }}>
                  <label style={{ ...s.label, color: '#166534', marginBottom: 10 }}>
                    Satuan
                    <span style={{ fontWeight: 400, fontSize: 12, marginLeft: 6, color: '#4ade80' }}>— untuk tampilan konversi di tabel stok</span>
                  </label>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
                    <div>
                      <label style={{ ...s.label, fontSize: 11 }}>Satuan Kecil</label>
                      <input style={s.input} list="unit-kecil-list" value={addonForm.unit}
                        onChange={e => setAddonForm({ ...addonForm, unit: e.target.value })}
                        placeholder="Pilih atau ketik..." />
                      <datalist id="unit-kecil-list">
                        <option value="pcs" />
                        <option value="btl" />
                        <option value="bks" />
                        <option value="gls" />
                        <option value="cup" />
                        <option value="kaleng" />
                        <option value="sachet" />
                        <option value="porsi" />
                        <option value="buah" />
                        <option value="lembar" />
                        <option value="roll" />
                        <option value="set" />
                        <option value="liter" />
                        <option value="kg" />
                      </datalist>
                    </div>
                    <div>
                      <label style={{ ...s.label, fontSize: 11 }}>Satuan Besar <span style={{ fontWeight: 400 }}>(opsional)</span></label>
                      <input style={s.input} list="unit-besar-list" value={addonForm.unit_besar}
                        onChange={e => setAddonForm({ ...addonForm, unit_besar: e.target.value })}
                        placeholder="Pilih atau ketik..." />
                      <datalist id="unit-besar-list">
                        <option value="dus" />
                        <option value="karton" />
                        <option value="krat" />
                        <option value="pak" />
                        <option value="lusin" />
                        <option value="gross" />
                        <option value="sak" />
                      </datalist>
                    </div>
                    <div>
                      <label style={{ ...s.label, fontSize: 11 }}>Pcs/Dus</label>
                      <input style={s.input} type="number" min={1} value={addonForm.konversi}
                        onChange={e => setAddonForm({ ...addonForm, konversi: e.target.value })}
                        placeholder="24" disabled={!addonForm.unit_besar} />
                    </div>
                  </div>
                  {addonForm.unit_besar && addonForm.konversi && (
                    <div style={{ marginTop: 8, fontSize: 12, color: '#166534' }}>
                      Contoh tampilan: <strong>24 {addonForm.unit || 'pcs'} = 1 {addonForm.unit_besar}</strong>
                    </div>
                  )}
                </div>
              )}
              {(addonForm.tipe === 'konsumsi' || addonForm.tipe === 'peralatan') && (
                <div style={{ marginBottom: 16, padding: '12px 14px', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8 }}>
                  <label style={{ ...s.label, color: '#92400e' }}>
                    Link ke Produk Stok
                    <span style={{ fontWeight: 400, fontSize: 12, marginLeft: 6 }}>(opsional — untuk link ke tabel produk global)</span>
                  </label>
                  <select style={s.input} value={addonForm.product_id} onChange={e => setAddonForm({ ...addonForm, product_id: e.target.value })}>
                    <option value="">— Tanpa link produk —</option>
                    {addonProducts.map(p => <option key={p.id} value={p.id}>{p.name} [{p.sku}] · {p.unit}</option>)}
                  </select>
                </div>
              )}
              {/* Foto Produk */}
              <div style={{ marginBottom: 16 }}>
                <label style={s.label}>Foto Produk (opsional)</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                  {addonImagePreview && (
                    <img src={addonImagePreview} alt="preview" style={{ width: 72, height: 72, objectFit: 'cover', borderRadius: 8, border: '1px solid #e2e8f0' }} />
                  )}
                  <div>
                    <input type="file" accept="image/*" id="addon-img-input" style={{ display: 'none' }}
                      onChange={e => {
                        const file = e.target.files?.[0];
                        if (file) {
                          setAddonImageFile(file);
                          const reader = new FileReader();
                          reader.onload = ev => setAddonImagePreview(ev.target?.result as string);
                          reader.readAsDataURL(file);
                        }
                      }}
                    />
                    <label htmlFor="addon-img-input" style={{ cursor: 'pointer', padding: '8px 14px', background: '#f1f5f9', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 13, fontWeight: 600, color: '#334155' }}>
                      📷 {addonImagePreview ? 'Ganti Foto' : 'Pilih Foto'}
                    </label>
                    {addonImageFile && <div style={{ fontSize: 11, color: '#16a34a', marginTop: 4 }}>✓ {addonImageFile.name}</div>}
                  </div>
                </div>
              </div>

              <div style={{ display: 'flex', gap: 10 }}>
                <button style={s.btnPrimary} onClick={saveAddon} disabled={addonSaving}>
                  {addonSaving ? 'Menyimpan...' : '💾 Simpan'}
                </button>
                <button style={s.btnOutline} onClick={cancelAddonForm}>Batal</button>
              </div>
            </div>
            </div>
          )}

          {/* Add-Ons Table */}
          <div style={s.tableCard}>
            {addonLoading ? <p style={s.emptyText}>Memuat...</p>
              : addons.length === 0 ? (
                <div style={s.emptyState}>
                  <p style={s.emptyTitle}>Belum ada add-on</p>
                  <p style={s.emptySub}>Klik "+ Tambah Add-On" untuk menambahkan</p>
                </div>
              ) : filteredAddons.length === 0 ? (
                <div style={s.emptyState}>
                  <p style={s.emptyTitle}>Tidak ditemukan</p>
                  <p style={s.emptySub}>Coba kata kunci lain</p>
                </div>
              ) : (
                <div style={{ overflowX: 'auto' }}>
                  <table style={s.table}>
                    <thead>
                      <tr style={s.thead}>
                        <th style={s.th}>Nama Add-On</th>
                        <th style={s.th}>Kode</th>
                        <th style={s.th}>Tipe</th>
                        <th style={{ ...s.th, textAlign: 'right' as const }}>Harga</th>
                        <th style={{ ...s.th, textAlign: 'center' as const }}>Status</th>
                        <th style={s.th}>Aksi</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredAddons.map(a => (
                        <tr key={a.id} style={{ ...s.tr, opacity: a.is_active ? 1 : 0.5 }}>
                          <td style={s.td}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                              {a.image_url
                                ? <img src={`${API}/pricing/addons/${a.id}/image`} alt="" style={{ width: 36, height: 36, objectFit: 'cover', borderRadius: 6, border: '1px solid #e2e8f0', flexShrink: 0 }} onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                                : <div style={{ width: 36, height: 36, background: '#f1f5f9', borderRadius: 6, border: '1px dashed #cbd5e1', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16 }}>📷</div>
                              }
                              <div>
                                <strong style={{ color: '#1e293b' }}>{a.name}</strong>
                                {a.description && <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>{a.description}</div>}
                                {!a.is_active && <span style={{ fontSize: 11, color: '#e74c3c' }}>(nonaktif)</span>}
                              </div>
                            </div>
                          </td>
                          <td style={s.td}>
                            {(a as any).kode_barang
                              ? <span style={{ fontFamily: 'monospace', fontSize: 12, background: '#f1f5f9', padding: '2px 6px', borderRadius: 4, color: '#334155' }}>{(a as any).kode_barang}</span>
                              : <span style={{ color: '#cbd5e1', fontSize: 12 }}>—</span>}
                          </td>
                          <td style={s.td}>
                            {(() => {
                              const t = a.addon_type || (a.is_rental !== false ? 'jasa' : 'konsumsi');
                              const bg = t === 'fnb' ? '#fff3e0' : t === 'konsumsi' || t === 'peralatan' ? '#fef9c3' : '#f3e8ff';
                              const color = t === 'fnb' ? '#e65100' : t === 'konsumsi' || t === 'peralatan' ? '#854d0e' : '#9b59b6';
                              const label = t === 'fnb' ? '🍔 FNB' : t === 'konsumsi' || t === 'peralatan' ? '🥤 Konsumsi' : '🎾 Jasa';
                              return <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 20, fontSize: 11, fontWeight: 600, background: bg, color }}>{label}</span>;
                            })()}
                          </td>
                          <td style={{ ...s.td, textAlign: 'right' as const, fontWeight: 600 }}>{fmt(a.weekday_price)}</td>
                          <td style={{ ...s.td, textAlign: 'center' as const }}>
                            <button onClick={() => toggleAddonActive(a)} style={a.is_active ? s.badgeActive : s.badgeInactive}>
                              {a.is_active ? '✅ Aktif' : '⏸ Nonaktif'}
                            </button>
                          </td>
                          <td style={s.td}>
                            <div style={{ display: 'flex', gap: 6 }}>
                              <button style={s.editBtn} onClick={() => openEditAddon(a)}>✏️ Edit</button>
                              <button style={s.deleteBtn} onClick={() => deleteAddon(a)}>🗑️</button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
          </div>
        </div>
      )}

      {/* ═══════════════ TAB: STOK KONSUMSI ═══════════════ */}
      {tab === 'stok' && (
        <div>
          <div style={s.actionBar}>
            <p style={s.actionBarText}>
              {filteredStok.length} produk konsumsi ·{' '}
              <span style={{ color: '#e74c3c' }}>{filteredStok.filter(a => (a.chiller_quantity ?? 0) === 0 && (a.gudang_quantity ?? 0) === 0).length} habis total</span>
              {' · '}
              <span style={{ color: '#d97706' }}>{filteredStok.filter(a => (a.chiller_quantity ?? 0) === 0 && (a.gudang_quantity ?? 0) > 0).length} perlu transfer</span>
            </p>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <input
                style={s.searchInput}
                placeholder="Cari produk..."
                value={searchStok}
                onChange={e => setSearchStok(e.target.value)}
              />
              <button style={s.btnPrimary} onClick={() => { setShowMutForm(true); setMutError(''); setMutForm(emptyMutForm); }}>
                + Input Stok
              </button>
            </div>
          </div>

          {/* Mutation Modal */}
          {showMutForm && (
            <div style={s.overlay}>
              <div style={s.modal}>
                <h2 style={{ margin: '0 0 20px', fontSize: 18, color: '#1e293b' }}>📦 Input Mutasi Stok</h2>
                {mutError && <div style={s.mutErrorBox}>{mutError}</div>}
                <form onSubmit={submitMutation} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                  <div>
                    <label style={s.label}>Produk Add-On Konsumsi *</label>
                    <select style={s.input} value={mutForm.addonId} onChange={e => setMutForm({ ...mutForm, addonId: e.target.value, jumlahDus: '', jumlahPcs: '' })} required>
                      <option value="">— Pilih Produk —</option>
                      {konsumsiAddons.map(a => (
                        <option key={a.id} value={a.id}>{a.name} (Gudang: {a.gudang_quantity ?? 0} · Chiller: {a.chiller_quantity ?? 0})</option>
                      ))}
                    </select>
                    {konsumsiAddons.length === 0 && (
                      <p style={{ fontSize: 12, color: '#e74c3c', margin: '6px 0 0' }}>
                        Belum ada produk konsumsi. Tambahkan add-on dengan tipe "Konsumsi" di tab Daftar Add-Ons dulu.
                      </p>
                    )}
                    {/* Detail konversi produk yang dipilih */}
                    {mutForm.addonId && (() => {
                      const sel = konsumsiAddons.find(a => a.id === mutForm.addonId);
                      if (!sel) return null;
                      const k = sel.konversi && sel.konversi > 1 ? sel.konversi : null;
                      return (
                        <div style={{ marginTop: 8, padding: '8px 12px', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 6, fontSize: 12, color: '#166534' }}>
                          {k ? <>📦 {k} {sel.unit || 'pcs'} = 1 {sel.unit_besar || 'dus'} (konversi terkunci)</> : <>📦 Satuan: {sel.unit || 'pcs'} (tidak ada konversi Dus)</>}
                        </div>
                      );
                    })()}
                  </div>
                  <div>
                    <label style={s.label}>Tipe Mutasi *</label>
                    <select style={s.input} value={mutForm.type} onChange={e => setMutForm({ ...mutForm, type: e.target.value })}>
                      <option value="in">📦 Masuk Gudang (dari supplier)</option>
                      <option value="transfer">↕️ Transfer Gudang → Chiller</option>
                      <option value="out_gudang">📤 Keluar Gudang (rusak/hilang/koreksi)</option>
                      <option value="out">🧊 Keluar Chiller (koreksi manual chiller)</option>
                    </select>
                    {mutForm.type === 'in' && (
                      <div style={{ marginTop: 6, padding: '6px 10px', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 6, fontSize: 12, color: '#15803d', fontWeight: 600 }}>
                        📦 Stok GUDANG akan BERTAMBAH
                      </div>
                    )}
                    {mutForm.type === 'transfer' && (
                      <div style={{ marginTop: 6, padding: '6px 10px', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 6, fontSize: 12, color: '#1d4ed8', fontWeight: 600 }}>
                        ↕️ GUDANG BERKURANG · CHILLER BERTAMBAH sejumlah ini
                      </div>
                    )}
                    {mutForm.type === 'out_gudang' && (
                      <div style={{ marginTop: 6, padding: '6px 10px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 6, fontSize: 12, color: '#b91c1c', fontWeight: 600 }}>
                        📦 Stok GUDANG akan BERKURANG
                      </div>
                    )}
                    {mutForm.type === 'out' && (
                      <div style={{ marginTop: 6, padding: '6px 10px', background: '#fff7ed', border: '1px solid #fed7aa', borderRadius: 6, fontSize: 12, color: '#c2410c', fontWeight: 600 }}>
                        🧊 Stok CHILLER akan BERKURANG (bukan gudang)
                      </div>
                    )}
                  </div>
                  {/* Jumlah Dus + PCS */}
                  {(() => {
                    const sel = konsumsiAddons.find(a => a.id === mutForm.addonId);
                    const hasDus = sel?.konversi && sel.konversi > 1 && sel.unit_besar;
                    return (
                      <div style={{ display: 'grid', gridTemplateColumns: hasDus ? '1fr 1fr' : '1fr', gap: 14 }}>
                        {hasDus && (
                          <div>
                            <label style={s.label}>Jumlah {sel!.unit_besar?.toUpperCase() || 'DUS'}</label>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                              <input style={s.input} type="number" min={0} value={mutForm.jumlahDus}
                                onChange={e => setMutForm({ ...mutForm, jumlahDus: e.target.value })}
                                placeholder="0" />
                              <span style={{ fontSize: 13, color: '#64748b', whiteSpace: 'nowrap' as const }}>{sel!.unit_besar || 'dus'}</span>
                            </div>
                          </div>
                        )}
                        <div>
                          <label style={s.label}>Jumlah {sel?.unit?.toUpperCase() || 'PCS'} {hasDus && <span style={{ fontWeight: 400, color: '#94a3b8' }}>(opsional)</span>}</label>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <input style={s.input} type="number" min={0} value={mutForm.jumlahPcs}
                              onChange={e => setMutForm({ ...mutForm, jumlahPcs: e.target.value })}
                              placeholder="0" />
                            <span style={{ fontSize: 13, color: '#64748b', whiteSpace: 'nowrap' as const }}>{sel?.unit || 'pcs'}</span>
                          </div>
                        </div>
                      </div>
                    );
                  })()}
                  {/* Total preview */}
                  {mutForm.addonId && (parseInt(mutForm.jumlahDus) > 0 || parseInt(mutForm.jumlahPcs) > 0) && (() => {
                    const sel = konsumsiAddons.find(a => a.id === mutForm.addonId);
                    const k = sel?.konversi && sel.konversi > 1 ? sel.konversi : 1;
                    const total = (parseInt(mutForm.jumlahDus) || 0) * k + (parseInt(mutForm.jumlahPcs) || 0);
                    return (
                      <div style={{ padding: '8px 12px', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 6, fontSize: 13, color: '#1d4ed8', fontWeight: 600 }}>
                        Total: {total} {sel?.unit || 'pcs'}
                      </div>
                    );
                  })()}
                  <div>
                    <label style={s.label}>Catatan</label>
                    <input style={s.input} value={mutForm.notes} onChange={e => setMutForm({ ...mutForm, notes: e.target.value })}
                      placeholder="Contoh: Terima 2 dus Prima dari supplier" />
                  </div>
                  <div style={{ display: 'flex', gap: 10, marginTop: 4 }}>
                    <button type="submit" disabled={mutSaving} style={{ ...s.btnPrimary, flex: 1, padding: '11px 0' }}>
                      {mutSaving ? 'Menyimpan...' : 'Simpan'}
                    </button>
                    <button type="button" onClick={() => setShowMutForm(false)} style={{ ...s.btnOutline, flex: 1, padding: '11px 0' }}>Batal</button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* Transfer Modal */}
          {transferModal && (() => {
            const { gudangQty, konversi, unitBesar, unit, addonName } = transferModal;
            const hasDus = konversi > 1 && unitBesar;
            const dus = parseInt(transferDus) || 0;
            const pcs = parseInt(transferPcs) || 0;
            const totalQty = hasDus ? (dus * konversi + pcs) : pcs;
            const gudangDus = hasDus ? Math.floor(gudangQty / konversi) : 0;
            const gudangPcs = hasDus ? gudangQty % konversi : gudangQty;
            const canTransfer = totalQty >= 1 && totalQty <= gudangQty;
            return (
            <div style={s.overlay}>
              <div style={{ ...s.modal, maxWidth: 420 }}>
                <h2 style={{ margin: '0 0 6px', fontSize: 18, color: '#1e293b' }}>↓ Transfer ke Chiller</h2>
                <p style={{ margin: '0 0 16px', fontSize: 13, color: '#64748b' }}>
                  Pindahkan stok <strong>{addonName}</strong> dari Gudang ke Chiller
                </p>
                <div style={{ background: '#f8fafc', borderRadius: 8, padding: '12px 16px', marginBottom: 20, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontSize: 13, color: '#64748b' }}>Tersedia di Gudang</span>
                  <strong style={{ fontSize: 15, color: '#1e293b' }}>
                    {hasDus ? `${gudangDus} ${unitBesar} ${gudangPcs} ${unit} (${gudangQty} ${unit})` : `${gudangQty} ${unit}`}
                  </strong>
                </div>
                {hasDus ? (
                  <>
                    <div style={{ display: 'flex', gap: 10, marginBottom: 12 }}>
                      <div style={{ flex: 1 }}>
                        <label style={s.label}>{unitBesar.toUpperCase()}</label>
                        <input
                          style={{ ...s.input, fontSize: 18, textAlign: 'center', fontWeight: 700 }}
                          type="number" min={0} max={gudangDus}
                          value={transferDus}
                          onChange={e => setTransferDus(e.target.value)}
                          placeholder="0"
                          autoFocus
                        />
                      </div>
                      <div style={{ flex: 1 }}>
                        <label style={s.label}>{unit.toUpperCase()} (ecer)</label>
                        <input
                          style={{ ...s.input, fontSize: 18, textAlign: 'center', fontWeight: 700 }}
                          type="number" min={0} max={konversi - 1}
                          value={transferPcs}
                          onChange={e => setTransferPcs(e.target.value)}
                          placeholder="0"
                        />
                      </div>
                    </div>
                    {totalQty > 0 && (
                      <div style={{ background: '#eff6ff', borderRadius: 8, padding: '10px 14px', marginBottom: 16, fontSize: 13, color: '#1d4ed8', fontWeight: 600 }}>
                        ↕️ Total dipindah: <strong>{totalQty} {unit}</strong> → Chiller bertambah {totalQty} {unit}
                      </div>
                    )}
                  </>
                ) : (
                  <div style={{ marginBottom: 20 }}>
                    <label style={s.label}>Jumlah yang dipindah ({unit}) *</label>
                    <input
                      style={{ ...s.input, fontSize: 18, textAlign: 'center', fontWeight: 700 }}
                      type="number" min={1} max={gudangQty}
                      value={transferPcs}
                      onChange={e => setTransferPcs(e.target.value)}
                      placeholder={`maks ${gudangQty}`}
                      autoFocus
                    />
                  </div>
                )}
                <div style={{ display: 'flex', gap: 10 }}>
                  <button
                    onClick={handleTransfer}
                    disabled={transferSaving || !canTransfer}
                    style={{ ...s.btnPrimary, flex: 1, padding: '11px 0', background: '#d97706', opacity: !canTransfer ? 0.5 : 1 }}>
                    {transferSaving ? 'Memproses...' : `↓ Transfer ${totalQty > 0 ? totalQty + ' ' + unit : '—'} ke Chiller`}
                  </button>
                  <button onClick={() => setTransferModal(null)} style={{ ...s.btnOutline, flex: 1, padding: '11px 0' }}>Batal</button>
                </div>
              </div>
            </div>
            );
          })()}

          {/* Stock Table */}
          <div style={s.tableCard}>
            {konsumsiAddons.length === 0 ? (
              <div style={s.emptyState}>
                <p style={s.emptyTitle}>Belum ada produk konsumsi</p>
                <p style={s.emptySub}>Buka tab "Daftar Add-Ons" dan tambahkan add-on dengan tipe 🥤 Konsumsi/Barang</p>
              </div>
            ) : filteredStok.length === 0 ? (
              <div style={s.emptyState}>
                <p style={s.emptyTitle}>Tidak ditemukan</p>
                <p style={s.emptySub}>Coba kata kunci lain</p>
              </div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={s.table}>
                  <thead>
                    <tr style={s.thead}>
                      <th style={s.th}>Nama Produk</th>
                      <th style={s.th}>Harga</th>
                      <th style={{ ...s.th, textAlign: 'center' as const }}>Gudang</th>
                      <th style={{ ...s.th, textAlign: 'center' as const }}>Chiller</th>
                      <th style={{ ...s.th, textAlign: 'center' as const }}>Transfer → Chiller</th>
                      <th style={{ ...s.th, textAlign: 'center' as const }}>Status</th>
                      <th style={s.th}>Aksi</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredStok.map(a => {
                      const gudang = a.gudang_quantity ?? 0;
                      const chiller = a.chiller_quantity ?? 0;
                      const threshold = 5;
                      const isOutTotal = chiller === 0 && gudang === 0;
                      const isNeedTransfer = chiller === 0 && gudang > 0;
                      const isOut = chiller === 0;
                      const chillerLow = chiller > 0 && chiller <= threshold;
                      const gudangLow = gudang > 0 && gudang <= threshold;
                      const showKonversi = a.unit_besar && a.konversi && a.konversi > 1;
                      return (
                        <tr key={a.id} style={{ ...s.tr, background: isNeedTransfer ? '#fffbeb' : isOutTotal ? '#fff5f5' : 'white' }}>
                          <td style={s.td}>
                            <strong style={{ color: '#1e293b' }}>{a.name}</strong>
                            {!a.is_active && <span style={{ fontSize: 11, color: '#94a3b8', marginLeft: 6 }}>(nonaktif)</span>}
                            {a.description && <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>{a.description}</div>}
                            {(a as any).kode_barang && <div style={{ fontSize: 11, fontFamily: 'monospace', color: '#475569', marginTop: 2 }}>#{(a as any).kode_barang}</div>}
                          </td>
                          <td style={s.td}>
                            <div style={{ fontSize: 12, fontWeight: 600 }}>{fmt(a.weekday_price)}</div>
                          </td>
                          <td style={{ ...s.td, textAlign: 'center' as const }}>
                            {showKonversi ? (
                              <>
                                <span style={{ fontSize: 20, fontWeight: 800, color: gudang === 0 ? '#94a3b8' : '#1e293b' }}>
                                  {Math.floor(gudang / a.konversi!)}
                                </span>
                                <div style={{ fontSize: 11, fontWeight: 700, color: '#64748b' }}>{(a.unit_besar || 'dus').toUpperCase()}</div>
                                <div style={{ fontSize: 10, color: '#94a3b8' }}>{gudang} {a.unit}</div>
                              </>
                            ) : (
                              <span style={{ fontSize: 20, fontWeight: 800, color: gudang === 0 ? '#94a3b8' : '#1e293b' }}>{gudang}</span>
                            )}
                          </td>
                          <td style={{ ...s.td, textAlign: 'center' as const }}>
                            <span style={{ fontSize: 20, fontWeight: 800, color: isOut ? '#e74c3c' : chillerLow ? '#f39c12' : '#16a34a' }}>
                              {chiller}
                            </span>
                            <div style={{ fontSize: 11, fontWeight: 700, color: '#64748b' }}>{(a.unit || 'pcs').toUpperCase()}</div>
                          </td>
                          <td style={{ ...s.td, textAlign: 'center' as const }}>
                            <button
                              disabled={gudang === 0}
                              onClick={() => { setTransferModal({ addonId: a.id, addonName: a.name, gudangQty: gudang, konversi: a.konversi && a.konversi > 1 ? a.konversi : 1, unitBesar: a.unit_besar || '', unit: a.unit || 'pcs' }); setTransferDus(''); setTransferPcs(''); }}
                              style={{
                                padding: '6px 12px', borderRadius: 6, fontSize: 12, fontWeight: 700, cursor: gudang === 0 ? 'not-allowed' : 'pointer',
                                border: gudang === 0 ? '1px solid #e2e8f0' : '1px solid #fbbf24',
                                background: gudang === 0 ? '#f8fafc' : '#fef3c7',
                                color: gudang === 0 ? '#94a3b8' : '#92400e',
                              }}>
                              {gudang === 0 ? 'Gudang Kosong' : `↓ Isi Chiller`}
                            </button>
                          </td>
                          <td style={{ ...s.td, textAlign: 'center' as const, display: 'flex', gap: 6, flexDirection: 'column' as const, alignItems: 'flex-start' as const }}>
                            {isNeedTransfer && <span style={s.badgeTransfer}>Perlu Transfer</span>}
                            {isOutTotal && <span style={s.badgeOut}>Habis Total</span>}
                            {!isOutTotal && !isNeedTransfer && chillerLow && <span style={s.badgeLow}>Chiller Menipis</span>}
                            {!isOutTotal && gudangLow && <span style={{ ...s.badgeLow, background: '#fed7aa', color: '#9a3412' }}>Gudang Menipis</span>}
                            {!isOutTotal && !isNeedTransfer && !chillerLow && !gudangLow && <span style={s.badgeOk}>Tersedia</span>}
                          </td>
                          <td style={s.td}>
                            <button style={s.editBtn} onClick={() => openMutFormForAddon(a)}>✏️ Mutasi</button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

        </div>
      )}
    </div>
  );
};

const s: Record<string, React.CSSProperties> = {
  container: { padding: 24, background: '#f5f7fa', minHeight: '100vh' },
  centered: { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100vh' },
  spinner: { width: 36, height: 36, border: '3px solid #e2e8f0', borderTop: '3px solid #1A73E8', borderRadius: '50%' },

  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#fff', padding: '20px 24px', borderRadius: 12, marginBottom: 16, boxShadow: '0 1px 4px rgba(0,0,0,0.08)', border: '1px solid #e2e8f0' },
  headerTitle: { margin: 0, fontSize: 22, fontWeight: 700, color: '#1e293b' },
  headerSub: { margin: '4px 0 0', fontSize: 13, color: '#64748b' },
  branchSelect: { padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14, color: '#374151', minWidth: 220 },
  branchBadge: { padding: '8px 14px', background: '#EEF4FF', color: '#1A73E8', borderRadius: 8, fontSize: 14, fontWeight: 600 },

  msg: { padding: '12px 16px', borderRadius: 8, marginBottom: 12, fontSize: 14, fontWeight: 600 },

  tabBar: { display: 'flex', gap: 4, marginBottom: 16, background: '#fff', padding: '6px 8px', borderRadius: 10, boxShadow: '0 1px 4px rgba(0,0,0,0.06)', border: '1px solid #e2e8f0' },
  tabBtn: { padding: '9px 20px', border: 'none', borderRadius: 7, cursor: 'pointer', fontSize: 14, fontWeight: 500, background: 'transparent', color: '#555', display: 'flex', alignItems: 'center', gap: 8 },
  tabActive: { background: '#1A73E8', color: 'white', fontWeight: 700 },
  tabCount: { padding: '1px 8px', borderRadius: 20, background: 'rgba(255,255,255,0.25)', fontSize: 12 },

  actionBar: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 },
  actionBarText: { margin: 0, fontSize: 13, color: '#64748b' },
  searchInput: { padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 13, fontFamily: 'inherit', width: 200, boxSizing: 'border-box' as const },

  btnPrimary: { padding: '9px 16px', background: '#1A73E8', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600 },
  btnOutline: { padding: '9px 16px', background: '#fff', color: '#374151', border: '1px solid #d1d5db', borderRadius: 8, cursor: 'pointer', fontSize: 13 },

  formCard: { background: '#fff', borderRadius: 12, padding: 20, marginBottom: 16, border: '2px solid #bfdbfe' },
  formTitle: { margin: '0 0 16px', fontSize: 16, fontWeight: 700, color: '#1e293b' },
  formGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 16 },
  label: { fontSize: 13, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 6 },
  input: { padding: '9px 12px', border: '1px solid #ddd', borderRadius: 6, fontSize: 14, fontFamily: 'inherit', width: '100%', boxSizing: 'border-box' as const },

  tableCard: { background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', overflow: 'hidden', marginBottom: 16 },
  table: { width: '100%', borderCollapse: 'collapse' as const },
  thead: { background: '#f8fafc' },
  th: { padding: '11px 16px', textAlign: 'left' as const, fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase' as const, letterSpacing: 0.5, borderBottom: '2px solid #e2e8f0', whiteSpace: 'nowrap' as const },
  tr: { borderBottom: '1px solid #f1f5f9' },
  td: { padding: '12px 16px', fontSize: 13, color: '#374151', verticalAlign: 'middle' as const },
  emptyCell: { textAlign: 'center' as const, padding: 40, color: '#94a3b8' },
  emptyState: { padding: 48, textAlign: 'center' as const },
  emptyTitle: { fontSize: 16, fontWeight: 600, color: '#64748b', margin: '0 0 8px' },
  emptySub: { fontSize: 13, color: '#94a3b8', margin: 0 },
  emptyText: { padding: 24, textAlign: 'center' as const, color: '#64748b' },

  badgeActive: { padding: '4px 10px', background: '#d1fae5', color: '#065f46', border: '1px solid #6ee7b7', borderRadius: 12, fontSize: 12, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' as const },
  badgeInactive: { padding: '4px 10px', background: '#f1f5f9', color: '#64748b', border: '1px solid #e2e8f0', borderRadius: 12, fontSize: 12, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' as const },
  badgeOk: { padding: '3px 10px', background: '#d1fae5', color: '#065f46', borderRadius: 20, fontSize: 11, fontWeight: 600 },
  badgeLow: { padding: '3px 10px', background: '#fef9c3', color: '#854d0e', borderRadius: 20, fontSize: 11, fontWeight: 600 },
  badgeOut: { padding: '3px 10px', background: '#fee2e2', color: '#dc2626', borderRadius: 20, fontSize: 11, fontWeight: 600 },
  badgeTransfer: { padding: '3px 10px', background: '#fef3c7', color: '#92400e', borderRadius: 20, fontSize: 11, fontWeight: 600 },
  editBtn: { padding: '5px 10px', background: '#eff6ff', color: '#1d4ed8', border: '1px solid #bfdbfe', borderRadius: 6, cursor: 'pointer', fontSize: 12, fontWeight: 600 },
  deleteBtn: { padding: '5px 8px', background: '#fee2e2', color: '#dc2626', border: '1px solid #fca5a5', borderRadius: 6, cursor: 'pointer', fontSize: 12 },

  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000 },
  modal: { background: 'white', borderRadius: 10, padding: 28, width: '100%', maxWidth: 480, boxShadow: '0 20px 60px rgba(0,0,0,0.3)' },
  mutErrorBox: { background: '#fee', color: '#c33', padding: '10px 14px', borderRadius: 6, marginBottom: 14, fontSize: 13 },
};
